/**
 * AgreementReviewStep - Final review before creating agreement
 *
 * Shows complete summary with option to edit sections
 * Creates the agreement in Firestore and navigates to preview
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getFirestore, collection, addDoc, doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { FlowNavigation, StepContainer, SummaryCard, AmountDisplay, FlowDivider } from '../FlowComponents';

const AgreementReviewStep = ({ data, updateData, goBack, goToStep }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!currentUser) {
      toast.error('Please log in to create an agreement');
      return;
    }

    setIsCreating(true);

    try {
      const db = getFirestore();

      // Create the agreement document
      const agreementData = {
        title: data.agreementTitle || `Agreement with ${data.clientName}`,
        text: data.agreementContent,
        status: 'draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userId: currentUser.uid,
        // Client info
        clientName: data.clientName,
        clientEmail: data.clientEmail,
        clientId: data.client?.id || null,
        // Project details
        scope: data.scope,
        price: parseFloat(data.price) || 0,
        currency: data.currency || 'USD',
        timeline: data.timeline,
        // Event details
        eventDetails: {
          venue: data.venue || '',
          startTime: data.startTime || '',
          duration: data.duration || '',
          specialRequirements: data.specialRequirements || '',
        },
        // Signature fields
        signatureFields: [],
        signatures: {},
        dualSignature: {
          enabled: false,
          client1Email: data.clientEmail,
          client2Email: '',
          bothRequired: false,
        },
        // Creation method
        creationMethod: data.agreementType,
      };

      const docRef = await addDoc(collection(db, 'agreements'), agreementData);

      // If client is new, add them to contacts
      if (data.client?.isNew) {
        try {
          await addDoc(collection(db, 'users', currentUser.uid, 'clients'), {
            name: data.clientName,
            email: data.clientEmail,
            company: data.client?.company || '',
            createdAt: serverTimestamp(),
          });
        } catch (err) {
          console.error('Error adding new client:', err);
          // Non-critical, don't fail the whole operation
        }
      }

      // Update client stats if existing client
      if (data.client?.id) {
        try {
          const clientRef = doc(db, 'users', currentUser.uid, 'clients', data.client.id);
          await updateDoc(clientRef, {
            'stats.agreements': (data.client.stats?.agreements || 0) + 1,
            lastAgreementDate: serverTimestamp(),
          });
        } catch (err) {
          console.error('Error updating client stats:', err);
        }
      }

      toast.success('Agreement created successfully!');
      navigate(`/agreement/${docRef.id}`);
    } catch (error) {
      console.error('Error creating agreement:', error);
      toast.error('Failed to create agreement. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  // Preview of agreement content (truncated)
  const contentPreview = data.agreementContent
    ? data.agreementContent.slice(0, 300) + (data.agreementContent.length > 300 ? '...' : '')
    : '';

  return (
    <StepContainer>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Agreement Title */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-medium text-gray-900 dark:text-white">
            {data.agreementTitle || `Agreement with ${data.clientName}`}
          </h3>
          <p className="text-gray-500 dark:text-[#666666] mt-1">
            Review your agreement before creating
          </p>
        </div>

        {/* Client Summary */}
        <SummaryCard title="Client">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFB27D] to-[#FF8F3F] flex items-center justify-center text-black font-medium">
                {data.clientName?.charAt(0).toUpperCase() || '?'}
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {data.clientName}
                </div>
                <div className="text-sm text-gray-500 dark:text-[#666666]">
                  {data.clientEmail}
                </div>
              </div>
            </div>
            <button
              onClick={() => goToStep(1)}
              className="text-sm text-brand hover:text-[#FFA164] transition-colors"
            >
              Edit
            </button>
          </div>
          {data.client?.isNew && (
            <div className="mt-3 flex items-center gap-2 text-xs text-brand">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Will be added to your contacts
            </div>
          )}
        </SummaryCard>

        {/* Service Details */}
        <SummaryCard title="Service Details">
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="text-sm text-gray-500 dark:text-[#666666] mb-1">Scope of Work</div>
                <div className="text-gray-900 dark:text-white">{data.scope}</div>
              </div>
              <button
                onClick={() => goToStep(2)}
                className="text-sm text-brand hover:text-[#FFA164] transition-colors ml-4"
              >
                Edit
              </button>
            </div>

            <FlowDivider />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500 dark:text-[#666666] mb-1">Total Price</div>
                <AmountDisplay
                  amount={parseFloat(data.price) || 0}
                  currency={data.currency || 'USD'}
                  size="large"
                />
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-[#666666] mb-1">Project Date</div>
                <div className="text-gray-900 dark:text-white font-medium">
                  {data.timeline ? new Date(data.timeline).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }) : 'Not set'}
                </div>
              </div>
            </div>

            {/* Event details if provided */}
            {(data.venue || data.startTime || data.duration) && (
              <>
                <FlowDivider />
                <div className="space-y-2 text-sm">
                  {data.venue && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400 dark:text-[#666666]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-gray-700 dark:text-[#999999]">{data.venue}</span>
                    </div>
                  )}
                  {data.startTime && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400 dark:text-[#666666]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-700 dark:text-[#999999]">{data.startTime}</span>
                    </div>
                  )}
                  {data.duration && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400 dark:text-[#666666]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-700 dark:text-[#999999]">{data.duration}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </SummaryCard>

        {/* Agreement Content Preview */}
        <SummaryCard title="Agreement Content">
          <div className="space-y-4">
            <div className="text-sm text-gray-700 dark:text-[#999999] leading-relaxed">
              {contentPreview}
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-[#1A1A1A]">
              <span className="text-xs text-gray-500 dark:text-[#666666]">
                {data.agreementContent?.split(/\s+/).filter(Boolean).length || 0} words
              </span>
              <button
                onClick={() => goToStep(3)}
                className="text-sm text-brand hover:text-[#FFA164] transition-colors"
              >
                Edit Content
              </button>
            </div>
          </div>
        </SummaryCard>

        {/* What happens next */}
        <div className="bg-gray-50 dark:bg-[#0A0A0A] rounded-2xl p-6 border border-gray-200 dark:border-[#1A1A1A]">
          <h4 className="font-medium text-gray-900 dark:text-white mb-4">What happens next?</h4>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium text-brand">1</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-[#999999]">
                Your agreement will be saved as a draft
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium text-brand">2</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-[#999999]">
                You can preview and make final edits before sending
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium text-brand">3</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-[#999999]">
                Send to {data.clientName} for signature
              </div>
            </div>
          </div>
        </div>

        {/* Create Button */}
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={handleCreate}
          disabled={isCreating}
          className={`w-full py-4 rounded-2xl font-medium transition-all flex items-center justify-center gap-2 ${
            isCreating
              ? 'bg-gray-100 dark:bg-[#1A1A1A] text-gray-400 dark:text-[#666666] cursor-wait'
              : 'bg-brand hover:bg-brand-hover text-black'
          }`}
        >
          {isCreating ? (
            <>
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              <span>Creating Agreement...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Create Agreement</span>
            </>
          )}
        </motion.button>

        {/* Back navigation */}
        <div className="pt-2">
          <FlowNavigation onBack={goBack} disabled={true} />
        </div>
      </div>
    </StepContainer>
  );
};

export default AgreementReviewStep;
